/**
 * File upload widget — single or multiple, producing FileJSON values.
 */

import type { FunctionComponent } from 'preact'
import { useCallback } from 'preact/hooks'
import { type FileJSON, fileJSONSize, formatBytes, parseAnyFileFormat } from '../../core/file-value'
import { PickerButton } from './PickerButton'
import { usePickFiles } from './use-pick-files'

interface FileInputProps {
  value: unknown
  onChange: (value: FileJSON | FileJSON[] | null) => void
  accept?: string | null
  maxSizeMB?: number | null
  multiple?: boolean
  disabled?: boolean
}

/** Normalize whatever is in the form state into the list this widget draws. */
function toFiles(value: unknown): FileJSON[] {
  const raw = Array.isArray(value) ? value : [value]
  return raw.map(parseAnyFileFormat).filter((f): f is FileJSON => f !== null)
}

export const FileInput: FunctionComponent<FileInputProps> = ({
  value,
  onChange,
  accept,
  maxSizeMB,
  multiple = false,
  disabled,
}) => {
  const files = toFiles(value)

  const commit = useCallback(
    (next: FileJSON[]) => onChange(multiple ? next : (next[0] ?? null)),
    [multiple, onChange]
  )

  const { inputRef, handlePick, reading, error, setError } = usePickFiles(
    maxSizeMB,
    (read) => commit(multiple ? [...files, ...read] : read.slice(0, 1)),
    'Filen'
  )

  const removeAt = (index: number) => {
    setError(null)
    commit(files.filter((_, i) => i !== index))
  }

  return (
    <div class="syv-file">
      <input
        ref={inputRef}
        type="file"
        accept={accept || undefined}
        multiple={multiple}
        disabled={disabled}
        onChange={handlePick}
        class="syv-file-native"
      />

      {files.length > 0 && (
        <ul class="syv-file-list">
          {files.map((file, index) => (
            <li class="syv-file-chip" key={`${file.filename}-${index}`}>
              <span class="syv-file-name">{file.filename || 'Fil'}</span>
              <span class="syv-file-size">{formatBytes(fileJSONSize(file))}</span>
              {!disabled && (
                <button
                  type="button"
                  class="syv-icon-button"
                  aria-label={`Fjern ${file.filename || 'fil'}`}
                  onClick={() => removeAt(index)}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {(multiple || files.length === 0) && (
        <PickerButton
          label={multiple && files.length > 0 ? 'Tilføj flere filer' : 'Vælg fil…'}
          reading={reading}
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        />
      )}

      {error && <div class="syv-error">{error}</div>}
      {maxSizeMB ? <span class="syv-hint">Maks. {maxSizeMB} MB pr. fil</span> : null}
    </div>
  )
}
